import React from 'react';
import { IntegrationIcon } from '@/shared/icons';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared/icons'
/**
 * Selectable provider card for provider selection step
 *
 * @example
 * <ProviderCard
 *   providerId="whatsapp-business"
 *   name="WhatsApp Business"
 *   description="Conecte sua conta do WhatsApp Business API"
 *   selected={selectedProvider === 'whatsapp-business'}
 *   onClick={() => handleSelect('whatsapp-business')}
 * />
 */
export interface ProviderCardProps {
  /** Provider ID */
  providerId: string;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Whether this provider is selected */
  selected?: boolean;
  /** Click handler */
  onClick: () => void;
}

export const ProviderCard: React.FC<ProviderCardProps> = ({
  providerId,
  name,
  description,
  selected = false,
  onClick,
}) => {
  const inDevelopmentProviders = ['telegram-bot', 'email-smtp'];
  const isInDevelopment = inDevelopmentProviders.includes(providerId);

  return (
    <button
      onClick={isInDevelopment ? undefined : onClick}
      disabled={isInDevelopment}
      className={cn(
        'relative w-full p-4 rounded-lg border-2 transition-all text-left',
        isInDevelopment
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2',
        selected ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
      )}
      style={{
        backgroundColor: selected
          ? 'rgba(139, 92, 246, 0.05)'
          : '#ffffff',
        borderColor: selected
          ? 'var(--theme-button-primary)'
          : '#e5e7eb',
        ...(isInDevelopment && {
          borderLeft: '4px solid #f97316',
        }),
      }}
    >
      {/* Selected indicator */}
      {selected && (
        <div
          className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: 'var(--theme-button-primary)',
          }}
        >
          <Icon name="check" size={14} className="text-white" />
        </div>
      )}

      {/* Badge "Em Desenvolvimento" */}
      {isInDevelopment && (
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium"
          style={{
            backgroundColor: '#fed7aa',
            color: '#c2410c',
          }}
        >
          Em Dev
        </div>
      )}

      {/* Provider icon */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0">
          <IntegrationIcon id={providerId} size={28} variant="brand-soft" />
        </div>
        <h3
          className="font-semibold text-base"
          style={{
            // ✅ Sempre texto escuro para contraste
            color: selected
              ? 'var(--theme-button-primary)' // Roxo quando selecionado
              : '#1e293b', // Preto suave padrão
          }}
        >
          {name}
        </h3>
      </div>

      {/* Description */}
      <p
        className="text-sm"
        style={{
          // ✅ Cinza médio para boa leitura
          color: '#64748b'
        }}
      >
        {description}
      </p>
    </button>
  );
};

ProviderCard.displayName = 'ProviderCard';
